import type { HTMLAttributes, ReactNode, ThHTMLAttributes, TdHTMLAttributes } from 'react';
import { cn } from '../cn';

export function Table({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="border-ink-200 overflow-x-auto rounded-lg border">
      <table className={cn('w-full text-sm', className)} {...props} />
    </div>
  );
}

export function THead({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      className={cn(
        'bg-ink-50 text-2xs text-ink-600 font-semibold uppercase tracking-wider',
        className,
      )}
      {...props}
    />
  );
}

export function TBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn('divide-ink-100 divide-y bg-white', className)} {...props} />;
}

export function TR({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn('hover:bg-ink-50/50 transition-colors', className)} {...props} />;
}

export function TH({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return <th className={cn('px-4 py-3 text-left', className)} {...props} />;
}

export function TD({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn('text-ink-800 px-4 py-3 align-middle', className)} {...props} />;
}

/** Cell para números — usa monospace + tabular-nums. */
export function TDNum({ className, children, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      className={cn('text-ink-900 px-4 py-3 text-right font-mono tabular-nums', className)}
      {...props}
    >
      {children}
    </td>
  );
}

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-8 py-16 text-center',
        className,
      )}
    >
      {icon && <div className="text-ink-400">{icon}</div>}
      <div>
        <h3 className="font-display text-ink-900 text-base font-semibold">{title}</h3>
        {description && <p className="text-ink-500 mt-1 text-sm">{description}</p>}
      </div>
      {action}
    </div>
  );
}
